'use client';

import {
  autoUpdate,
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import classNames from 'classnames';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type { MouseEvent } from 'react';

import { EVENT_NAMES, trackEvent } from '@/analytics';
import { MenuButtonContent } from '@/components/SiteHeader/MenuButtonContent';
import { assignTopicMenuGroupColor } from '@/components/SiteHeader/sharedConfig';
import { Typography } from '@/components/Typography';
import { topicPath } from '@/helpers/linkHelpers';
import ArrowRight from '@/icons/react/ArrowRight';
import type { SiteNavMenuItemType } from '@/lib/firestore/dataFetchers/getSiteNavMenu';
import { sortTitlesAPStyle } from '@/lib/sortTitlesAPStyle';
import { useSiteHeader } from '@/providers';

interface SiteHeaderDropdownProps {
  id: string;
  category: SiteNavMenuItemType;
  childrenTopics: SiteNavMenuItemType[];
  comingSoonTopics: SiteNavMenuItemType[];
  menuIndex: number;
}

export function SiteHeaderDropdown({
  id,
  category,
  childrenTopics,
  comingSoonTopics,
  menuIndex,
}: SiteHeaderDropdownProps) {
  const { activeDropdown, setActiveDropdown, siteHeaderMenuRef } =
    useSiteHeader();
  const menuRect = siteHeaderMenuRef?.current?.getBoundingClientRect();
  const yOffset = (menuRect?.top || 0) + (menuRect?.height || 145);
  const pathname = usePathname();
  const isOpen = activeDropdown === id;

  const accentColors = assignTopicMenuGroupColor(menuIndex);

  const { refs, context, floatingStyles } = useFloating({
    strategy: 'fixed',
    transform: false,
    open: isOpen,
    onOpenChange: (open) => setActiveDropdown(open ? id : null),
    middleware: [
      offset(0),
      shift({
        padding: 8,
      }),
    ],
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  // add event param typed correctly
  function handleDropdownClick(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();

    setActiveDropdown(isOpen ? null : id);

    trackEvent(EVENT_NAMES.site_nav_dropdown_opened, {
      dropdown_label: category.title,
      topic_slug: category.slug,
      menu_position_index: menuIndex.toString(), // TODO adjust if needed when Daily News is present
      window_size: 'desktop',
    });
  }

  function handleTopicClick(
    topicSlug: string,
    categorySlug: string,
    label: string,
    index?: number,
  ) {
    setActiveDropdown(null);

    const level = categorySlug ? 'parent' : 'child';

    trackEvent(EVENT_NAMES.site_nav_topic_click, {
      link_label: label,
      topic_slug: topicSlug,
      topic_level: level,
      parent_topic_slug: categorySlug,
      path: categorySlug ? topicPath(categorySlug) : topicPath(topicSlug),
      menu_position_index: index?.toString(),
      window_size: 'desktop',
    });
  }

  function isActivePath() {
    const isTopicPage = pathname?.split('/')[1] === 'topics';
    const currentTopicSlug = pathname?.split('/')[2];

    const isCategory = category?.slug === currentTopicSlug;
    const isChild = childrenTopics?.some(
      (topic) => topic.slug === currentTopicSlug,
    );

    if ((isChild || isCategory) && isTopicPage) return true;

    return false;
  }

  const isActive = isActivePath();

  function ChildTopicLinkColumns({
    childTopics,
    columns = 2,
  }: {
    childTopics?: SiteNavMenuItemType[];
    columns?: 2 | 3;
  }) {
    if (!childTopics?.length) return null;

    const sortedTopics = childTopics.sort((a, b) =>
      sortTitlesAPStyle(a.title, b.title),
    );

    return (
      <ul
        className={classNames('-mx-3 -mb-3', {
          'columns-2': columns === 2,
          'columns-3': columns === 3,
        })}
      >
        {sortedTopics.map((topic, index) => (
          <li key={topic.id} className="block">
            <Link
              href={topicPath(topic.slug)}
              className="flex items-center justify-between gap-3 py-2 px-3 group hover:bg-gray-100 dark:hover:bg-black rounded-2 transition-colors"
              onClick={() =>
                handleTopicClick(topic.slug, '', topic?.title, index + 1)
              }
            >
              {topic.title}
              <ArrowRight className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <>
      <Link
        ref={refs.setReference}
        {...getReferenceProps()}
        className="block relative"
        onClick={handleDropdownClick}
        href={topicPath(category?.slug)}
      >
        <MenuButtonContent open={isOpen} isActive={isActive}>
          {category?.title}
        </MenuButtonContent>
      </Link>

      {isOpen && (
        <FloatingPortal id="floating-ui-portal">
          <FloatingOverlay
            className="bg-black/75 dark:bg-gray-600/75 z-[99]"
            lockScroll
            style={{ top: yOffset }}
          />
          <FloatingFocusManager context={context}>
            <div
              ref={refs.setFloating}
              {...getFloatingProps()}
              className="flex flex-col gap-2 py-2 z-100"
              style={{
                ...floatingStyles,
                maxHeight: `calc(100dvh - ${yOffset}px)`,
              }}
            >
              <Link
                className={classNames(
                  'rounded-2 flex items-center justify-between gap-3 p-5 shadow-lg group',
                  accentColors.bg,
                )}
                href={topicPath(category?.slug)}
                onClick={() =>
                  handleTopicClick('', category.slug, category.title, 0)
                }
              >
                <div className="flex flex-col gap-half">
                  <Typography
                    name="heading-400"
                    className="group-hover:underline"
                  >
                    View All {category?.title}
                  </Typography>
                </div>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <div className="bg-paper dark:bg-gray-800 p-5 rounded-2 shadow-lg flex flex-col gap-8 overflow-y-auto">
                <div className="flex flex-col gap-2">
                  <Typography name="tag-350" className="text-secondary">
                    Dive into {category?.title}
                  </Typography>
                  <ChildTopicLinkColumns
                    childTopics={childrenTopics}
                    columns={childrenTopics?.length > 14 ? 3 : 2}
                  />
                </div>
                {!!comingSoonTopics?.length && (
                  <div className="flex flex-col gap-2">
                    <Typography name="tag-350" className="text-secondary">
                      Coming Soon
                    </Typography>
                    <Typography
                      className="text-secondary italic"
                      name="body-400"
                    >
                      {comingSoonTopics.map((topic) => topic.title).join(', ')}{' '}
                      and much more
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
